import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as Portal from '@radix-ui/react-portal';
import useModalA11y from '../../hooks/useModalA11y';
import { useStore } from '../../store/store';
import { requestSessionClaim } from '../../utils/sessionClaim';

/**
 * Blocking take-over modal (TD-2239). Opened by SessionClaimController when the
 * login claim is denied (the company is at its concurrent-session cap).
 * Claim-driven: oldestEmail + maxConcurrentUsers come from the claim response
 * (no /session-list call).
 *
 *  - Take over → POST /session-claim { force: true }. On success the caller now
 *    holds a slot → onTakeoverSuccess (proceed into the app).
 *  - Cancel (or Escape) → onCancel — the controller signs the user out to login.
 *
 * There is no backdrop dismiss: at the cap the user must make an explicit
 * choice (take over vs sign out).
 */
const SessionLimitModal = ({ isOpen, oldestEmail, onTakeoverSuccess, onCancel }) => {
  const store = useStore();
  const dialogRef = useRef(null);
  const takingOverRef = useRef(false);
  const [takingOver, setTakingOver] = useState(false);
  const [takeoverError, setTakeoverError] = useState(null);

  // Escape closes = cancel = sign out — but not while a take-over is in flight
  // (otherwise Escape could log the user out after the force-claim already
  // kicked a colleague).
  useModalA11y({
    isOpen,
    onClose: () => {
      if (!takingOverRef.current) onCancel();
    },
    dialogRef
  });

  // The modal stays mounted (returns null when closed), so its take-over state
  // would otherwise leak across open/close cycles — after one successful
  // take-over the button would re-open stuck on "Taking over…". Reset on every
  // open so each appearance starts clean (TD-2239).
  useEffect(() => {
    if (isOpen) {
      takingOverRef.current = false;
      setTakingOver(false);
      setTakeoverError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleTakeover = async () => {
    takingOverRef.current = true;
    setTakingOver(true);
    setTakeoverError(null);
    const res = await requestSessionClaim(store, true);
    if (res.allowed) {
      onTakeoverSuccess?.();
    } else {
      takingOverRef.current = false;
      setTakingOver(false);
      setTakeoverError('Could not take over a session. Please try again.');
    }
  };

  // Generic copy — intentionally does NOT expose the company's seat/licence count.
  const slotsLine = "Your organization's session limit is reached.";

  return (
    <Portal.Root>
      <div className="session-limit-modal-backdrop" role="presentation">
        <div
          className="session-limit-modal-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="session-limit-modal-title"
          ref={dialogRef}
          tabIndex={-1}
        >
          <h3 id="session-limit-modal-title" className="session-limit-modal-title">
            Session limit reached
          </h3>
          <div className="session-limit-modal-info">
            <p>{slotsLine}</p>
            {oldestEmail && (
              <p>
                Least recently active session: <strong>{oldestEmail}</strong>
              </p>
            )}
            <p>Take over to continue here, or cancel to sign out.</p>
          </div>
          {takeoverError && (
            <p className="session-limit-modal-error" role="alert">
              {takeoverError}
            </p>
          )}
          <div className="session-limit-modal-actions">
            <button
              type="button"
              className="session-limit-modal-btn-secondary"
              onClick={onCancel}
              disabled={takingOver}
            >
              Cancel
            </button>
            <button
              type="button"
              className="session-limit-modal-btn-primary"
              onClick={handleTakeover}
              disabled={takingOver}
            >
              {takingOver ? 'Taking over…' : 'Take over the session'}
            </button>
          </div>
        </div>
      </div>
    </Portal.Root>
  );
};

SessionLimitModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  oldestEmail: PropTypes.string,
  onTakeoverSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default SessionLimitModal;
